import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { priceTiers, products as PRODUCTS } from "@/data/pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CustomerLite = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
};

export const evenDistribution = (total: number): Record<string, number> => {
  const ids = PRODUCTS.map((p) => p.id);
  const base = Math.floor(total / ids.length);
  const remainder = total - base * ids.length;
  const q: Record<string, number> = {};
  ids.forEach((pid, i) => {
    q[pid] = base + (i < remainder ? 1 : 0);
  });
  return q;
};

const labelFor = (c: CustomerLite) =>
  c.company || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the sheet creates for this customer and hides the picker. */
  userId?: string;
  /** Display name used in the dialog description when userId is set. */
  customerName?: string;
  onCreated?: () => void;
}

const CreateSubscriptionSheet = ({
  open,
  onOpenChange,
  userId,
  customerName,
  onCreated,
}: Props) => {
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string>(userId ?? "");

  const [tier, setTier] = useState<number>(16);
  const [nickname, setNickname] = useState("");
  const [pmType, setPmType] = useState<"card" | "invoice">("invoice");
  const [addr, setAddr] = useState({ address: "", city: "", postalCode: "", country: "SE" });
  const [isPilot, setIsPilot] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>(() => evenDistribution(16));

  useEffect(() => {
    setSelectedUserId(userId ?? "");
  }, [userId, open]);

  useEffect(() => {
    if (!open || userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, company")
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(500);
      if (!cancelled) setCustomers((data as CustomerLite[]) || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 30);
    return customers
      .filter((c) =>
        `${c.first_name ?? ""} ${c.last_name ?? ""} ${c.company ?? ""} ${c.email}`
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 30);
  }, [customers, customerQuery]);

  const totalQty = Object.values(quantities).reduce((a, b) => a + Number(b || 0), 0);

  const setProductQty = (pid: string, next: number) =>
    setQuantities((prev) => ({ ...prev, [pid]: Math.max(0, Math.floor(next)) }));

  const handleTierChange = (n: number) => {
    setTier(n);
    setQuantities(evenDistribution(n));
  };

  const handlePilotToggle = (on: boolean) => {
    setIsPilot(on);
    setQuantities(evenDistribution(on ? 0 : tier));
  };

  const reset = () => {
    setNickname("");
    setAddr({ address: "", city: "", postalCode: "", country: "SE" });
    setIsPilot(false);
    setTier(16);
    setQuantities(evenDistribution(16));
    setCustomerQuery("");
    setSelectedUserId(userId ?? "");
  };

  const handleCreate = async () => {
    const targetUser = userId ?? selectedUserId;
    if (!targetUser) {
      toast.error("Välj en kund");
      return;
    }
    if (!addr.address || !addr.city || !addr.postalCode) {
      toast.error("Fyll i leveransadress");
      return;
    }

    let effectiveTier: number;
    let priceCents: number;

    if (isPilot) {
      if (totalQty < 1) {
        toast.error("Pilotpaket måste innehålla minst 1 tub");
        return;
      }
      if (totalQty > 96) {
        toast.error("Pilotpaket kan max innehålla 96 tuber");
        return;
      }
      effectiveTier = totalQty;
      priceCents = 0;
    } else {
      const t = priceTiers.find((x) => x.tubes === tier);
      if (!t) {
        toast.error("Ogiltigt paket");
        return;
      }
      if (totalQty !== tier) {
        toast.error(`Tuberna måste summera till ${tier} (just nu ${totalQty})`);
        return;
      }
      effectiveTier = tier;
      priceCents = t.subscriptionPrice * 100;
    }

    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-create-subscription", {
        body: {
          user_id: targetUser,
          tier_tubes: effectiveTier,
          quantities,
          monthly_price_cents: priceCents,
          shipping_address: addr,
          nickname: nickname.trim() || undefined,
          payment_method_type: isPilot ? "invoice" : pmType,
          is_pilot: isPilot,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(isPilot ? "Pilotpaket skapat" : "Prenumeration skapad");
      onOpenChange(false);
      reset();
      onCreated?.();
    } catch (e: any) {
      toast.error(e?.message || "Kunde inte skapa prenumeration");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Skapa ny prenumeration</DialogTitle>
          <DialogDescription>
            {customerName
              ? `Lägg till en anläggning åt ${customerName}. Första ordern hamnar som "Att skicka" i leveranser.`
              : 'Välj kund och paket. Prenumerationen skapas hos betalleverantören och första ordern hamnar som "Att skicka" i leveranser.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!userId && (
            <div>
              <Label>Kund</Label>
              <Input
                className="mb-2"
                placeholder="Sök på namn, företag eller e-post"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
              />
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj kund" />
                </SelectTrigger>
                <SelectContent>
                  {filteredCustomers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {labelFor(c)} — {c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-start gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
            <Switch
              id="is-pilot"
              checked={isPilot}
              onCheckedChange={handlePilotToggle}
              className="mt-0.5"
            />
            <div className="flex-1 -mt-0.5">
              <Label htmlFor="is-pilot" className="text-sm font-medium text-purple-900 cursor-pointer">
                Pilotpaket (gratis provkit)
              </Label>
              <p className="text-xs text-purple-800/80 mt-0.5">
                {isPilot
                  ? "Inget pris debiteras. Välj fritt antal tuber per produkt nedan (1–96 totalt)."
                  : "Aktivera om kunden ska få paketet kostnadsfritt som pilot."}
              </p>
            </div>
          </div>

          {!isPilot && (
            <div>
              <Label>Paket</Label>
              <Select value={String(tier)} onValueChange={(v) => handleTierChange(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priceTiers.map((t) => (
                    <SelectItem key={t.tubes} value={String(t.tubes)}>
                      {t.name} — {t.tubes} tuber ({t.subscriptionPrice} kr/mån)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Tuber per produkt</Label>
              <span
                className={`text-xs font-medium ${
                  isPilot
                    ? totalQty >= 1 && totalQty <= 96
                      ? "text-emerald-700"
                      : "text-destructive"
                    : totalQty === tier
                      ? "text-emerald-700"
                      : "text-destructive"
                }`}
              >
                {isPilot ? `${totalQty} tuber` : `${totalQty} / ${tier} tuber`}
              </span>
            </div>
            <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
              {PRODUCTS.map((p) => {
                const v = quantities[p.id] ?? 0;
                return (
                  <div key={p.id} className="flex items-center justify-between gap-3">
                    <span className="text-sm">{p.name}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setProductQty(p.id, v - 1)}
                        disabled={v <= 0}
                      >
                        −
                      </Button>
                      <span className="w-8 text-center text-sm tabular-nums">{v}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setProductQty(p.id, v + 1)}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Namn på anläggning (valfritt)</Label>
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={80}
              placeholder="t.ex. Kontor Stockholm"
            />
          </div>

          {!isPilot && (
            <div>
              <Label>Betalmetod</Label>
              <Select value={pmType} onValueChange={(v) => setPmType(v as "card" | "invoice")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Faktura</SelectItem>
                  <SelectItem value="card">Sparat kort (om kunden har)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Leveransadress</Label>
            <Input
              className="mb-2"
              placeholder="Gatuadress"
              value={addr.address}
              onChange={(e) => setAddr({ ...addr, address: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Postnummer"
                value={addr.postalCode}
                onChange={(e) => setAddr({ ...addr, postalCode: e.target.value })}
              />
              <Input
                placeholder="Ort"
                value={addr.city}
                onChange={(e) => setAddr({ ...addr, city: e.target.value })}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Avbryt
          </Button>
          <Button
            disabled={
              busy ||
              (!userId && !selectedUserId) ||
              (isPilot ? totalQty < 1 || totalQty > 96 : totalQty !== tier)
            }
            onClick={handleCreate}
          >
            {busy ? "Skapar…" : isPilot ? "Skapa pilotpaket" : "Skapa prenumeration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSubscriptionSheet;
